import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { Award, Download, CheckCircle, RefreshCw } from 'lucide-react';

const CertificateGenerator = ({ user, apiEndpoint, authToken }) => {
  const [eligibilityByVersion, setEligibilityByVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const PLATFORM_NAME = 'JavaOCP.ClusterMicro.com';
  const PASS_THRESHOLD = 20;
  const ATTENDANCE_THRESHOLD = 20;
  const QUESTIONS_PER_TEST = 50;
  const PASS_PERCENT = 65;

  useEffect(() => {
    fetchCertificateEligibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCertificateEligibility = async () => {
    setLoading(true);
    setError(null);

    try {
      const API_BASE =
        apiEndpoint ||
        process.env.REACT_APP_API_ENDPOINT;

      const response = await fetch(
        `${API_BASE}/java-cert-get-exam-history?email=${encodeURIComponent(user.email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken || ''}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📜 Certificate data received:', data);

      // Read per-version eligibility directly from Lambda response
      if (data.certificateEligibilityByVersion) {
        setEligibilityByVersion(data.certificateEligibilityByVersion);
      } else {
        // Defensive fallback if Lambda hasn't been updated yet
        const empty = {
          passedCount: 0,
          attendedCount: 0,
          eligibleForPassCertificate: false,
          eligibleForAttendanceCertificate: false,
          progressToPass: { current: 0, required: PASS_THRESHOLD, remaining: PASS_THRESHOLD, percentage: '0.0' },
          progressToAttendance: { current: 0, required: ATTENDANCE_THRESHOLD, remaining: ATTENDANCE_THRESHOLD, percentage: '0.0' },
        };
        setEligibilityByVersion({ java17: empty, java21: empty });
      }
    } catch (err) {
      console.error('❌ Failed to fetch certificate eligibility:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Java version metadata
  const versionMeta = {
    '17': {
      label: 'Java 17 OCP',
      examCode: '1Z0-829',
      fullTitle: 'Oracle Certified Professional: Java SE 17 Developer',
      passColor: { r: 16, g: 185, b: 129 },   // green
      passBg: { r: 240, g: 250, b: 240 },
      attendanceColor: { r: 5, g: 150, b: 105 },
      attendanceBg: { r: 236, g: 253, b: 245 },
      uiAccent: '#10b981',
      uiLight: '#d1fae5',
      uiBgLight: '#f0fdf4'
    },
 '21': {
  label: 'Java 21 OCP',
  examCode: '1Z0-830',
  fullTitle: 'Oracle Certified Professional: Java SE 21 Developer',
  passColor: { r: 64, g: 64, b: 64 },         // dark grey #404040
  passBg: { r: 248, g: 248, b: 248 },         // very light grey background
  attendanceColor: { r: 85, g: 85, b: 85 },   // medium grey #555555
  attendanceBg: { r: 245, g: 245, b: 245 },   // light grey background
  uiAccent: '#404040',                          // dark grey for accents
  uiLight: '#E8E8E8',                           // light grey for chips
  uiBgLight: '#F5F5F5'                          // soft grey background
}
  };

  // Generate Pass Certificate PDF for a specific Java version
  const generatePassCertificate = (version) => {
    const meta = versionMeta[version];
    const eligibility = eligibilityByVersion[`java${version}`];

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    // Background
    doc.setFillColor(meta.passBg.r, meta.passBg.g, meta.passBg.b);
    doc.rect(0, 0, 297, 210, 'F');

    // Outer border
    doc.setLineWidth(3);
    doc.setDrawColor(meta.passColor.r, meta.passColor.g, meta.passColor.b);
    doc.rect(10, 10, 277, 190);

    // Inner border
    doc.setLineWidth(1);
    doc.setDrawColor(meta.attendanceColor.r, meta.attendanceColor.g, meta.attendanceColor.b);
    doc.rect(15, 15, 267, 180);

    // Header
    doc.setFontSize(36);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(meta.passColor.r, meta.passColor.g, meta.passColor.b);
    doc.text('CERTIFICATE OF ACHIEVEMENT', 148.5, 38, { align: 'center' });

    // Subtitle — OCP exam this practice is for
    doc.setFontSize(15);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`${meta.label} Practice Test Series (${meta.examCode})`, 148.5, 50, { align: 'center' });

    // Body text
    doc.setFontSize(13);
    doc.setTextColor(80, 80, 80);
    doc.text('This is to certify that', 148.5, 68, { align: 'center' });

    // Name
    doc.setFontSize(30);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(meta.attendanceColor.r, meta.attendanceColor.g, meta.attendanceColor.b);
    doc.text((user.name || user.email).toUpperCase(), 148.5, 86, { align: 'center' });

    // Achievement text
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('has successfully passed', 148.5, 100, { align: 'center' });

    doc.setFontSize(17);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(meta.passColor.r, meta.passColor.g, meta.passColor.b);
    doc.text(
      `${eligibility.passedCount} Unique ${meta.label} Certification Test${eligibility.passedCount !== 1 ? 's' : ''}`,
      148.5, 112, { align: 'center' }
    );

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`with a minimum score of ${PASS_PERCENT}% in each exam`, 148.5, 122, { align: 'center' });

    // Practice disclaimer (legal protection — clarifies this is practice, not official Oracle cert)
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'italic');
    doc.text(
      `This certificate recognizes practice test performance on ${PLATFORM_NAME}.`,
      148.5, 134, { align: 'center' }
    );
    doc.text(
      `It is not an official Oracle certification.`,
      148.5, 140, { align: 'center' }
    );

    // Date
    doc.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text(`Issued on: ${currentDate}`, 148.5, 158, { align: 'center' });

    // Signature line
    doc.setLineWidth(0.5);
    doc.setDrawColor(150, 150, 150);
    doc.line(120, 172, 177, 172);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Authorized Signature', 148.5, 178, { align: 'center' });

    // Footer
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`${PLATFORM_NAME} | Java OCP Practice Platform`, 148.5, 188, { align: 'center' });

    // Certificate ID
    const certificateId = `OCP${version}-PASS-${(user.email || '').substring(0, 5).toUpperCase()}-${Date.now()}`;
    doc.text(`Certificate ID: ${certificateId}`, 148.5, 193, { align: 'center' });

    // Save
    const safeName = (user.name || user.email || 'User').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    doc.save(`Java${version}_OCP_Pass_Certificate_${safeName}.pdf`);
  };

  // Generate Attendance Certificate PDF for a specific Java version
  const generateAttendanceCertificate = (version) => {
    const meta = versionMeta[version];
    const eligibility = eligibilityByVersion[`java${version}`];

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    // Background (use attendance-bg variant)
    doc.setFillColor(meta.attendanceBg.r, meta.attendanceBg.g, meta.attendanceBg.b);
    doc.rect(0, 0, 297, 210, 'F');

    // Outer border
    doc.setLineWidth(3);
    doc.setDrawColor(meta.attendanceColor.r, meta.attendanceColor.g, meta.attendanceColor.b);
    doc.rect(10, 10, 277, 190);

    // Inner border
    doc.setLineWidth(1);
    doc.setDrawColor(meta.passColor.r, meta.passColor.g, meta.passColor.b);
    doc.rect(15, 15, 267, 180);

    // Header
    doc.setFontSize(36);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(meta.attendanceColor.r, meta.attendanceColor.g, meta.attendanceColor.b);
    doc.text('CERTIFICATE OF PARTICIPATION', 148.5, 38, { align: 'center' });

    // Subtitle
    doc.setFontSize(15);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`${meta.label} Practice Test Series (${meta.examCode})`, 148.5, 50, { align: 'center' });

    // Body text
    doc.setFontSize(13);
    doc.setTextColor(80, 80, 80);
    doc.text('This is to certify that', 148.5, 68, { align: 'center' });

    // Name
    doc.setFontSize(30);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(meta.passColor.r, meta.passColor.g, meta.passColor.b);
    doc.text((user.name || user.email).toUpperCase(), 148.5, 86, { align: 'center' });

    // Achievement text
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('has diligently completed', 148.5, 100, { align: 'center' });

    doc.setFontSize(17);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(meta.attendanceColor.r, meta.attendanceColor.g, meta.attendanceColor.b);
    doc.text(
      `${eligibility.attendedCount} Unique ${meta.label} Certification Test${eligibility.attendedCount !== 1 ? 's' : ''}`,
      148.5, 112, { align: 'center' }
    );

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`attempting all ${QUESTIONS_PER_TEST} questions in each exam`, 148.5, 122, { align: 'center' });

    doc.setFontSize(12);
    doc.text('demonstrating commitment to Java certification preparation', 148.5, 130, { align: 'center' });

    // Practice disclaimer
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.setFont('helvetica', 'italic');
    doc.text(
      `This certificate recognizes practice test attendance on ${PLATFORM_NAME}.`,
      148.5, 142, { align: 'center' }
    );
    doc.text(
      `It is not an official Oracle certification.`,
      148.5, 148, { align: 'center' }
    );

    // Date
    doc.setFont('helvetica', 'normal');
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    doc.text(`Issued on: ${currentDate}`, 148.5, 162, { align: 'center' });

    // Signature line
    doc.setLineWidth(0.5);
    doc.setDrawColor(150, 150, 150);
    doc.line(120, 175, 177, 175);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Authorized Signature', 148.5, 181, { align: 'center' });

    // Footer
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`${PLATFORM_NAME} | Java OCP Practice Platform`, 148.5, 188, { align: 'center' });

    // Certificate ID
    const certificateId = `OCP${version}-ATT-${(user.email || '').substring(0, 5).toUpperCase()}-${Date.now()}`;
    doc.text(`Certificate ID: ${certificateId}`, 148.5, 193, { align: 'center' });

    // Save
    const safeName = (user.name || user.email || 'User').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    doc.save(`Java${version}_OCP_Attendance_Certificate_${safeName}.pdf`);
  };

  // Render one card (Pass or Attendance) for one Java version
  const renderCard = (version, type) => {
    const meta = versionMeta[version];
    const eligibility = eligibilityByVersion[`java${version}`];
    const isPassCard = type === 'pass';

    const eligible = isPassCard
      ? eligibility.eligibleForPassCertificate
      : eligibility.eligibleForAttendanceCertificate;

    const progress = isPassCard
      ? eligibility.progressToPass
      : eligibility.progressToAttendance;

    const cardTitle = isPassCard
      ? `${meta.label} Pass Certificate`
      : `${meta.label} Attendance Certificate`;

    const cardIcon = isPassCard ? Award : CheckCircle;
    const CardIconComponent = cardIcon;

    const description = isPassCard
      ? <>Pass {PASS_THRESHOLD} or more <strong>{meta.label}</strong> certification tests with at least {PASS_PERCENT}% to earn this certificate.</>
      : <>Attempt all {QUESTIONS_PER_TEST} questions in {ATTENDANCE_THRESHOLD} or more <strong>{meta.label}</strong> certification tests to earn this certificate.</>;

    const downloadHandler = isPassCard
      ? () => generatePassCertificate(version)
      : () => generateAttendanceCertificate(version);

    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
        border: eligible ? `2px solid ${meta.uiAccent}` : '2px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '10px' }}>
          <CardIconComponent
            size={28}
            style={{ color: eligible ? meta.uiAccent : '#9ca3af', flexShrink: 0 }}
          />
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            margin: 0,
            color: '#1f2937',
            lineHeight: '1.3'
          }}>
            {cardTitle}
          </h3>
        </div>

        <p style={{
          fontSize: '13px',
          color: '#6b7280',
          marginBottom: '16px',
          lineHeight: '1.6',
          flex: 1
        }}>
          {description}
        </p>

        <div style={{
          backgroundColor: meta.uiBgLight,
          padding: '14px',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Progress:</span>
            <div style={{
              marginTop: '6px',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${progress.percentage}%`,
                backgroundColor: eligible ? meta.uiAccent : '#9ca3af',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
          <p style={{
            fontSize: '18px',
            fontWeight: 'bold',
            margin: 0,
            color: eligible ? meta.uiAccent : '#4b5563'
          }}>
            {progress.current} / {progress.required} Tests {isPassCard ? 'Passed' : 'Attended'}
          </p>
        </div>

        {eligible ? (
          <button
            onClick={downloadHandler}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: meta.uiAccent,
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Download size={18} />
            Download Certificate
          </button>
        ) : (
          <div style={{
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '13px',
            color: '#92400e',
            fontWeight: '500'
          }}>
            {isPassCard
              ? `Pass ${progress.remaining} more ${meta.label} test${progress.remaining !== 1 ? 's' : ''} to unlock`
              : `Complete ${progress.remaining} more ${meta.label} test${progress.remaining !== 1 ? 's' : ''} to unlock`}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={48} style={{ color: '#667eea', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading certificate data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '600px',
        margin: '40px auto',
        padding: '24px',
        backgroundColor: '#fef2f2',
        borderRadius: '12px',
        border: '1px solid #fecaca'
      }}>
        <p style={{ color: '#dc2626', marginBottom: '16px' }}>
          Failed to load certificate data: {error}
        </p>
        <button
          onClick={fetchCertificateEligibility}
          style={{
            padding: '12px 24px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!eligibilityByVersion) {
    return null;
  }

  return (
    <div style={{
      maxWidth: '1100px',
      margin: '0 auto',
      padding: '32px 20px'
    }}>
      {/* Mobile responsive override */}
      <style>{`
        @media (max-width: 768px) {
          .cert-grid {
            grid-template-columns: 1fr !important;
          }
          .cert-version-group {
            margin-bottom: 24px;
          }
        }
      `}</style>

      <h2 style={{
        fontSize: '28px',
        fontWeight: 'bold',
        marginBottom: '8px',
        color: '#1f2937'
      }}>
        📜 Certificates
      </h2>
      <p style={{
        fontSize: '15px',
        color: '#374151',
        marginBottom: '8px'
      }}>
        Earn separate certificates for <strong>Java 17 OCP</strong> and <strong>Java 21 OCP</strong> by completing <strong>Certification Tests</strong>.
      </p>
      <p style={{
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '32px',
        fontStyle: 'italic'
      }}>
        Only numbered Certification Tests count toward certificates. Random and Mixed practice exams are for learning only. Repeating the same test number counts once toward eligibility.
      </p>

      {/* Java 17 Section */}
      <div className="cert-version-group" style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '16px',
          paddingBottom: '10px',
          borderBottom: '2px solid #d1fae5'
        }}>
          <span style={{
            backgroundColor: '#d1fae5',
            color: '#065f46',
            padding: '4px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '700'
          }}>
            Java 17 OCP · 1Z0-829
          </span>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            {eligibilityByVersion.java17.attendedCount} unique attempt{eligibilityByVersion.java17.attendedCount !== 1 ? 's' : ''} so far
          </span>
        </div>

        <div className="cert-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px'
        }}>
          {renderCard('17', 'pass')}
          {renderCard('17', 'attendance')}
        </div>
      </div>

      {/* Java 21 Section */}
      <div className="cert-version-group" style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '16px',
          paddingBottom: '10px',
          borderBottom: '2px solid #d1d5db'
        }}>
          <span style={{
  backgroundColor: '#E8E8E8',
  color: '#404040',
  padding: '4px 12px',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: '700'
}}>
  Java 21 OCP · 1Z0-830
</span>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            {eligibilityByVersion.java21.attendedCount} unique attempt{eligibilityByVersion.java21.attendedCount !== 1 ? 's' : ''} so far
          </span>
        </div>

        <div className="cert-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px'
        }}>
          {renderCard('21', 'pass')}
          {renderCard('21', 'attendance')}
        </div>
      </div>

      {/* Stats Summary */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
        marginTop: '16px'
      }}>
        <h4 style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '16px',
          color: '#1f2937'
        }}>
          Your Statistics
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          <StatBox
            label="Java 17 Passed"
            value={eligibilityByVersion.java17.passedCount}
            color="#10b981"
            bg="#f0fdf4"
          />
          <StatBox
            label="Java 17 Attended"
            value={eligibilityByVersion.java17.attendedCount}
            color="#059669"
            bg="#ecfdf5"
          />
          <StatBox
  label="Java 21 Passed"
  value={eligibilityByVersion.java21.passedCount}
  color="#404040"
  bg="#F5F5F5"
/>
<StatBox
  label="Java 21 Attended"
  value={eligibilityByVersion.java21.attendedCount}
  color="#555555"
  bg="#E8E8E8"
/>
        </div>
        <p style={{
          fontSize: '12px',
          color: '#9ca3af',
          marginTop: '14px',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Pass = score ≥ {PASS_PERCENT}% • Attended = completed certification test with {QUESTIONS_PER_TEST} questions • Counts deduplicated by test number
        </p>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, color, bg }) => (
  <div style={{
    padding: '12px',
    backgroundColor: bg,
    borderRadius: '8px',
    textAlign: 'center'
  }}>
    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px 0', fontWeight: '500' }}>
      {label}
    </p>
    <p style={{ fontSize: '24px', fontWeight: 'bold', color, margin: 0 }}>
      {value}
    </p>
  </div>
);

export default CertificateGenerator;